import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Alert,
  Tag,
  Tooltip,
  InputNumber,
  Spin,
  Divider,
  Row,
  Col,
  Progress,
  List,
  Badge,
  Select,
  Popconfirm,
  Modal,
  Form,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  SwapOutlined,
  PlusOutlined,
  DeleteOutlined,
  SyncOutlined,
  WarningOutlined
} from '@ant-design/icons';
import {
  calculateMaterialRequirementsFromInvoice,
  checkSmartAvailability,
  generateAvailabilitySuggestions,
  getAvailabilityStatusDisplay,
  shouldShowMaterialList,
  AVAILABILITY_STATUS,
  AVAILABILITY_STATUS_LABELS,
  AVAILABILITY_STATUS_COLORS
} from '../../utils/enhancedAvailabilitySystem';
import {
  calculateMaterialRequirementsWithOverrides,
  getMaterialAlternatives,
  canOverrideMaterial,
  validateMaterialOverrides,
  getOverridableMaterials
} from '../../utils/inventoryCalculations';
import {
  generateMaterialListFromInvoice,
  refreshMaterialListAvailability,
  getMaterialListByInvoice,
  saveMaterialListFromCalculations,
  updateMaterialListFromCalculations,
  resetMaterialListToAuto
} from '../../firebase/materialListService';
import {
  getMaterialListState,
  getLineItemChanges,
  validateLineItemsForMaterials,
  MATERIAL_LIST_STATE
} from '../../utils/materialListUtils';
import { MATERIAL_TYPES } from '../../firebase/inventoryService';

const { Title, Text, Paragraph } = Typography;

/**
 * Material List Manager Component
 * Handles hybrid material requirements (dynamic vs saved) with change detection
 */
const MaterialListManager = ({ 
  invoice, 
  schedules = [],
  onMaterialListChange,
  readOnly = false,
  showTitle = true 
}) => {
  // State management
  const [materialRequirements, setMaterialRequirements] = useState({});
  const [savedMaterialList, setSavedMaterialList] = useState(null);
  const [materialListState, setMaterialListState] = useState(null);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingMaterials, setSavingMaterials] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [manualAdjustments, setManualAdjustments] = useState({});
  const [manualOverrides, setManualOverrides] = useState({});
  const [lastChecked, setLastChecked] = useState(null);
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  const [lineItemChanges, setLineItemChanges] = useState(null);
  const [addMaterialForm] = Form.useForm();

  // Memoized calculations
  const lineItemValidation = useMemo(() => {
    return validateLineItemsForMaterials(invoice?.lineItems);
  }, [invoice?.lineItems]);

  const shouldShow = useMemo(() => {
    return shouldShowMaterialList(invoice?.invoiceType) && lineItemValidation.isValid;
  }, [invoice?.invoiceType, lineItemValidation.isValid]);

  // Load saved material list and determine state
  useEffect(() => {
    if (!shouldShow || !invoice?.id) return;

    const loadMaterialList = async () => {
      try {
        console.log('🔍 Loading material list for invoice:', invoice.invoiceNumber);
        
        const savedList = await getMaterialListByInvoice(invoice.id);
        setSavedMaterialList(savedList);

        // Determine material list state
        const state = getMaterialListState(savedList, invoice.lineItems);
        setMaterialListState(state);

        if (savedList) {
          console.log(`📋 Found saved material list (${state.state}):`, savedList.id);
          
          // Load materials from saved list
          setMaterialRequirements(savedList.requirements || {});
          
          // Check for line item changes
          if (state.state === MATERIAL_LIST_STATE.OUTDATED) {
            const changes = getLineItemChanges(invoice.lineItems, savedList.lineItemsSnapshot);
            setLineItemChanges(changes);
          } else {
            setLineItemChanges(null);
          }
        } else {
          console.log('🔄 No saved material list found - calculating dynamically');
          
          // Calculate materials dynamically
          const result = calculateMaterialRequirementsWithOverrides(invoice.lineItems, manualOverrides);
          setMaterialRequirements(result.requirements);
          setLineItemChanges(null);
        }
      } catch (error) {
        console.error('❌ Error loading material list:', error);
      }
    };

    loadMaterialList();
  }, [invoice?.id, invoice?.lineItems, shouldShow, manualOverrides]);

  // Recalculate dynamic materials when line items change (only if no saved materials)
  useEffect(() => {
    if (!shouldShow || !invoice || savedMaterialList) return;

    console.log('🔄 Recalculating dynamic material requirements');
    
    try {
      const result = calculateMaterialRequirementsWithOverrides(invoice.lineItems, manualOverrides);
      setMaterialRequirements(result.requirements);
      
      // Trigger callback to parent
      if (onMaterialListChange) {
        onMaterialListChange({
          requirements: result.requirements,
          availabilityStatus: availabilityStatus?.status,
          lastChecked: new Date(),
          appliedOverrides: result.appliedOverrides,
          isDynamic: true
        });
      }
    } catch (error) {
      console.error('❌ Error calculating material requirements:', error);
    }
  }, [invoice?.lineItems, manualOverrides, shouldShow, savedMaterialList, onMaterialListChange, availabilityStatus?.status]);

  // Check availability when requirements or schedules change
  useEffect(() => {
    if (!shouldShow || Object.keys(materialRequirements).length === 0) return;

    checkAvailability();
  }, [materialRequirements, schedules, shouldShow]);

  // Check availability function
  const checkAvailability = useCallback(async () => {
    if (!invoice || Object.keys(materialRequirements).length === 0) return;

    setLoading(true);
    try {
      console.log('🔍 Checking material availability...');
      
      const result = await checkSmartAvailability(materialRequirements, invoice, schedules);
      setAvailabilityResult(result);
      setAvailabilityStatus(result);
      setLastChecked(new Date());

      // Generate suggestions if there are conflicts
      if (!result.isAvailable) {
        const availabilitySuggestions = await generateAvailabilitySuggestions(result, materialRequirements);
        setSuggestions(availabilitySuggestions);
      } else {
        setSuggestions([]);
      }

      console.log('✅ Availability check completed:', result.status);
    } catch (error) {
      console.error('❌ Error checking availability:', error);
      setAvailabilityStatus({
        status: AVAILABILITY_STATUS.ERROR,
        message: `Error checking availability: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  }, [invoice, materialRequirements, schedules]);

  // Manual refresh function
  const handleRefresh = useCallback(() => {
    checkAvailability();
  }, [checkAvailability]);

  // Handle manual quantity adjustments
  const handleQuantityChange = useCallback((materialType, newQuantity) => {
    if (readOnly) return;

    setManualAdjustments(prev => ({
      ...prev,
      [materialType]: {
        ...prev[materialType],
        quantity: newQuantity,
        isManuallyAdjusted: true
      }
    }));

    // Update requirements with manual adjustment
    setMaterialRequirements(prev => ({
      ...prev,
      [materialType]: {
        ...prev[materialType],
        totalQuantity: newQuantity,
        isManuallyAdjusted: true
      }
    }));
  }, [readOnly]);

  // Handle material override (e.g., WHEELS -> JACKBASE)
  const handleMaterialOverride = useCallback((originalMaterial, newMaterial) => {
    if (readOnly) return;

    setManualOverrides(prev => {
      const updated = { ...prev };
      if (newMaterial && newMaterial !== originalMaterial) {
        updated[originalMaterial] = newMaterial;
      } else {
        delete updated[originalMaterial];
      }
      return updated;
    });
  }, [readOnly]);

  // Handle adding new material
  const handleAddMaterial = useCallback(() => {
    addMaterialForm.validateFields().then(values => {
      const { materialType, quantity } = values;
      
      setMaterialRequirements(prev => ({
        ...prev,
        [materialType]: {
          materialType,
          totalQuantity: quantity,
          configurations: [],
          isManuallyAdded: true
        }
      }));

      setShowAddMaterialModal(false);
      addMaterialForm.resetFields();
      message.success('Material added successfully');
    });
  }, [addMaterialForm]);

  // Handle removing material
  const handleRemoveMaterial = useCallback((materialType) => {
    if (readOnly) return;

    setMaterialRequirements(prev => {
      const updated = { ...prev };
      delete updated[materialType];
      return updated;
    });

    // Also remove any overrides for this material
    setManualOverrides(prev => {
      const updated = { ...prev };
      delete updated[materialType];
      return updated;
    });

    message.success('Material removed successfully');
  }, [readOnly]);

  // Save manual adjustments
  const handleSaveAdjustments = useCallback(() => {
    setEditMode(false);
    checkAvailability(); // Re-check with new quantities
  }, [checkAvailability]);

  // Cancel manual adjustments
  const handleCancelAdjustments = useCallback(() => {
    setEditMode(false);
    // Reset to original calculated requirements
    const result = calculateMaterialRequirementsWithOverrides(invoice.lineItems, {});
    setMaterialRequirements(result.requirements);
    setManualAdjustments({});
    setManualOverrides({});
  }, [invoice]);

  // Handle saving materials from dynamic calculations
  const handleSaveMaterials = useCallback(async () => {
    // Enhanced validation checks
    if (!invoice?.id) {
      console.error('❌ Cannot save: Invoice ID is missing');
      message.error('Cannot save: Invoice not found');
      return;
    }

    if (!materialRequirements || Object.keys(materialRequirements).length === 0) {
      console.error('❌ Cannot save: No material requirements to save');
      message.error('Cannot save: No material requirements calculated');
      return;
    }

    if (!invoice.lineItems || invoice.lineItems.length === 0) {
      console.error('❌ Cannot save: No line items found');
      message.error('Cannot save: Invoice has no line items');
      return;
    }

    setSavingMaterials(true);
    try {
      console.log('💾 Saving material list from dynamic calculations');
      console.log('📋 Invoice ID:', invoice.id);
      console.log('📦 Material Requirements:', Object.keys(materialRequirements).length, 'types');
      console.log('📝 Line Items:', invoice.lineItems.length, 'items');
      
      await saveMaterialListFromCalculations(
        invoice.id,
        materialRequirements,
        invoice.lineItems,
        {
          availabilityCheck: availabilityResult,
          availabilityStatus: availabilityStatus?.status,
          manualAdjustments,
          manualOverrides
        }
      );

      // Reload the material list to update state
      console.log('🔄 Reloading saved material list...');
      const savedList = await getMaterialListByInvoice(invoice.id);
      setSavedMaterialList(savedList);
      
      const state = getMaterialListState(savedList, invoice.lineItems);
      setMaterialListState(state);
      setLineItemChanges(null);

      console.log('✅ Material list saved successfully');
      message.success('Material list saved successfully');
    } catch (error) {
      console.error('❌ Error saving material list:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        name: error.name,
        stack: error.stack
      });

      // Provide specific error messages based on error type
      let errorMessage = 'Failed to save material list';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied: You need admin privileges to save material lists';
        console.error('🔒 Permission denied - user may not have admin role');
      } else if (error.code === 'unauthenticated') {
        errorMessage = 'Authentication required: Please log in to save material lists';
        console.error('🔐 User not authenticated');
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service unavailable: Please check your internet connection';
        console.error('🌐 Network or service unavailable');
      } else if (error.message) {
        errorMessage = `Failed to save: ${error.message}`;
      }

      message.error(errorMessage, 5); // Show for 5 seconds
    } finally {
      setSavingMaterials(false);
    }
  }, [invoice, materialRequirements, availabilityResult, availabilityStatus, manualAdjustments, manualOverrides]);

  // Handle updating materials with new calculations
  const handleUpdateMaterials = useCallback(async () => {
    if (!savedMaterialList?.id || !invoice?.lineItems) return;

    setSavingMaterials(true);
    try {
      console.log('🔄 Updating material list with new calculations');
      
      // Calculate new requirements
      const result = calculateMaterialRequirementsWithOverrides(invoice.lineItems, manualOverrides);
      
      await updateMaterialListFromCalculations(
        savedMaterialList.id,
        result.requirements,
        invoice.lineItems,
        {
          availabilityCheck: availabilityResult,
          availabilityStatus: availabilityStatus?.status,
          manualAdjustments,
          manualOverrides
        }
      );

      // Update local state
      setMaterialRequirements(result.requirements);
      
      // Reload the material list to update state
      const updatedList = await getMaterialListByInvoice(invoice.id);
      setSavedMaterialList(updatedList);
      
      const state = getMaterialListState(updatedList, invoice.lineItems);
      setMaterialListState(state);
      setLineItemChanges(null);

      message.success('Material list updated successfully');
    } catch (error) {
      console.error('❌ Error updating material list:', error);
      message.error('Failed to update material list');
    } finally {
      setSavingMaterials(false);
    }
  }, [savedMaterialList, invoice, manualOverrides, availabilityResult, availabilityStatus, manualAdjustments]);

  // Handle resetting to auto-calculate mode
  const handleResetToAuto = useCallback(async () => {
    if (!invoice?.id) return;

    setSavingMaterials(true);
    try {
      console.log('🔄 Resetting material list to auto-calculate mode');
      
      await resetMaterialListToAuto(invoice.id);

      // Reset local state to dynamic mode
      setSavedMaterialList(null);
      setLineItemChanges(null);
      
      // Recalculate materials dynamically
      const result = calculateMaterialRequirementsWithOverrides(invoice.lineItems, manualOverrides);
      setMaterialRequirements(result.requirements);
      
      // Set state to dynamic
      const dynamicState = {
        state: MATERIAL_LIST_STATE.DYNAMIC,
        badge: 'Auto-Calculated',
        badgeColor: 'processing',
        description: 'Materials calculated from line items in real-time'
      };
      setMaterialListState(dynamicState);

      message.success('Material list reset to auto-calculate mode');
    } catch (error) {
      console.error('❌ Error resetting material list:', error);
      message.error('Failed to reset material list');
    } finally {
      setSavingMaterials(false);
    }
  }, [invoice, manualOverrides]);

  // Get display properties for availability status
  const statusDisplay = useMemo(() => {
    if (!availabilityStatus) return null;
    return getAvailabilityStatusDisplay(availabilityStatus.status);
  }, [availabilityStatus]);

  // Table columns for material requirements
  const columns = [
    {
      title: 'Material Type',
      dataIndex: 'materialType',
      key: 'materialType',
      render: (text, record) => {
        const isOverridable = canOverrideMaterial(text);
        const alternatives = getMaterialAlternatives(text);
        const currentOverride = manualOverrides[text];
        
        if (editMode && !readOnly && isOverridable) {
          return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>{text}</Text>
              <Select
                size="small"
                placeholder="Override with..."
                value={currentOverride}
                onChange={(value) => handleMaterialOverride(text, value)}
                allowClear
                style={{ width: '100%' }}
              >
                {alternatives.map(alt => (
                  <Select.Option key={alt} value={alt}>
                    <SwapOutlined style={{ marginRight: 4 }} />
                    {alt}
                  </Select.Option>
                ))}
              </Select>
            </Space>
          );
        }
        
        return (
          <Space>
            <Text strong>{currentOverride || text}</Text>
            {currentOverride && (
              <Tooltip title={`Overridden from ${text}`}>
                <SwapOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            )}
            {record.isManuallyAdded && (
              <Tooltip title="Manually added">
                <PlusOutlined style={{ color: '#52c41a' }} />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Required Quantity',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      render: (quantity, record) => {
        if (editMode && !readOnly) {
          return (
            <InputNumber
              min={0}
              value={quantity}
              onChange={(value) => handleQuantityChange(record.materialType, value)}
              style={{ width: '100%' }}
            />
          );
        }
        
        return (
          <Space>
            <Text>{quantity}</Text>
            {record.isManuallyAdjusted && (
              <Tooltip title="Manually adjusted">
                <EditOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Source',
      dataIndex: 'configurations',
      key: 'source',
      render: (configurations, record) => {
        if (record.isManuallyAdded) {
          return <Tag color="green">Manual</Tag>;
        }
        
        if (configurations && configurations.length > 0) {
          return (
            <Tooltip title={configurations.map(c => c.configurationName).join(', ')}>
              <Tag color="blue">{configurations.length} config(s)</Tag>
            </Tooltip>
          );
        }
        
        return <Tag color="default">Unknown</Tag>;
      }
    },
    ...(editMode && !readOnly ? [{
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.isManuallyAdded && (
            <Popconfirm
              title="Remove this material?"
              onConfirm={() => handleRemoveMaterial(record.materialType)}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                type="text" 
                danger 
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      )
    }] : [])
  ];

  // Prepare table data
  const tableData = useMemo(() => {
    return Object.entries(materialRequirements).map(([materialType, requirement]) => ({
      key: materialType,
      materialType,
      ...requirement
    }));
  }, [materialRequirements]);

  // Don't render if not applicable
  if (!shouldShow) {
    return null;
  }

  return (
    <Card 
      title={
        showTitle ? (
          <Space>
            <span>Material Requirements</span>
            {materialListState && (
              <Tag 
                color={materialListState.badgeColor}
                icon={
                  materialListState.state === MATERIAL_LIST_STATE.DYNAMIC ? <SyncOutlined /> :
                  materialListState.state === MATERIAL_LIST_STATE.SAVED ? <CheckCircleOutlined /> :
                  materialListState.state === MATERIAL_LIST_STATE.OUTDATED ? <WarningOutlined /> :
                  <InfoCircleOutlined />
                }
              >
                {materialListState.badge}
              </Tag>
            )}
          </Space>
        ) : null
      }
      extra={
        <Space>
          {lastChecked && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Last checked: {lastChecked.toLocaleTimeString()}
            </Text>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
          {!readOnly && (
            <>
              {/* Hybrid System Action Buttons */}
              {materialListState?.state === MATERIAL_LIST_STATE.DYNAMIC && (
                <Button
                  icon={<SaveOutlined />}
                  type="primary"
                  size="small"
                  onClick={handleSaveMaterials}
                  loading={savingMaterials}
                >
                  Save Material List
                </Button>
              )}
              
              {materialListState?.state === MATERIAL_LIST_STATE.SAVED && (
                <Popconfirm
                  title="Reset to auto-calculate mode?"
                  description="This will delete the saved materials and return to dynamic calculation."
                  onConfirm={handleResetToAuto}
                  okText="Yes, Reset"
                  cancelText="Cancel"
                >
                  <Button
                    icon={<SyncOutlined />}
                    size="small"
                    loading={savingMaterials}
                  >
                    Reset to Auto-Calculate
                  </Button>
                </Popconfirm>
              )}
              
              {materialListState?.state === MATERIAL_LIST_STATE.OUTDATED && (
                <Popconfirm
                  title="Reset to auto-calculate mode?"
                  description="This will delete the saved materials and return to dynamic calculation with current line items."
                  onConfirm={handleResetToAuto}
                  okText="Yes, Reset"
                  cancelText="Cancel"
                >
                  <Button
                    icon={<SyncOutlined />}
                    type="primary"
                    size="small"
                    loading={savingMaterials}
                  >
                    Reset to Auto-Calculate
                  </Button>
                </Popconfirm>
              )}

              {/* Edit Mode Buttons */}
              {editMode ? (
                <Space>
                  <Button
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={() => setShowAddMaterialModal(true)}
                  >
                    Add Material
                  </Button>
                  <Button
                    icon={<SaveOutlined />}
                    type="primary"
                    size="small"
                    onClick={handleSaveAdjustments}
                  >
                    Save
                  </Button>
                  <Button
                    icon={<CloseOutlined />}
                    size="small"
                    onClick={handleCancelAdjustments}
                  >
                    Cancel
                  </Button>
                </Space>
              ) : (
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => setEditMode(true)}
                >
                  Edit Materials
                </Button>
              )}
            </>
          )}
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {/* Material List State Description */}
      {materialListState && (
        <Alert
          message={materialListState.description}
          type={
            materialListState.state === MATERIAL_LIST_STATE.DYNAMIC ? 'info' :
            materialListState.state === MATERIAL_LIST_STATE.SAVED ? 'success' :
            materialListState.state === MATERIAL_LIST_STATE.OUTDATED ? 'warning' :
            'info'
          }
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Line Item Validation Alert */}
      {lineItemValidation && !lineItemValidation.hasValidItems && (
        <Alert
          message="Line Items Need Configuration"
          description={
            <div>
              <Text>{lineItemValidation.message}</Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  To calculate materials, please specify the following for each line item:
                </Text>
                <ul style={{ marginTop: 4, marginBottom: 0 }}>
                  <li><Text type="secondary">Working height (e.g., 2.5m, 4m, etc.)</Text></li>
                  <li><Text type="secondary">Width (Single or Double)</Text></li>
                  <li><Text type="secondary">Quantity</Text></li>
                </ul>
              </div>
            </div>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Partial Line Item Validation Alert */}
      {lineItemValidation && lineItemValidation.hasValidItems && lineItemValidation.validItemCount < lineItemValidation.totalItemCount && (
        <Alert
          message={lineItemValidation.message}
          description={
            <Text type="secondary">
              Some line items don't have working height and width specified. Materials will only be calculated for configured items.
            </Text>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Line Item Changes Alert */}
      {lineItemChanges && lineItemChanges.hasChanges && (
        <Alert
          message="Invoice Line Items Have Changed"
          description={
            <div>
              <Text>The following changes were detected since materials were last saved:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {lineItemChanges.changes.map((change, index) => (
                  <li key={index}>
                    <Text type={change.type === 'removed' ? 'danger' : change.type === 'added' ? 'success' : 'warning'}>
                      {change.description}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}
      {/* Availability Status Display */}
      {availabilityStatus && statusDisplay && (
        <Alert
          message={
            <Space>
              {React.createElement(
                statusDisplay.icon === 'CheckCircleOutlined' ? CheckCircleOutlined :
                statusDisplay.icon === 'InfoCircleOutlined' ? InfoCircleOutlined :
                statusDisplay.icon === 'ClockCircleOutlined' ? ClockCircleOutlined :
                statusDisplay.icon === 'ExclamationCircleOutlined' ? ExclamationCircleOutlined :
                statusDisplay.icon === 'CalendarOutlined' ? CalendarOutlined :
                QuestionCircleOutlined,
                { style: { color: statusDisplay.color } }
              )}
              <Text strong style={{ color: statusDisplay.color }}>
                {statusDisplay.label}
              </Text>
            </Space>
          }
          description={availabilityStatus.message}
          type={
            availabilityStatus.status === AVAILABILITY_STATUS.CONFIRMED_AVAILABLE ? 'success' :
            availabilityStatus.status === AVAILABILITY_STATUS.PRELIMINARY_AVAILABLE ? 'info' :
            availabilityStatus.status === AVAILABILITY_STATUS.SCHEDULE_PENDING ? 'warning' :
            availabilityStatus.status === AVAILABILITY_STATUS.CONFIRMED_CONFLICT ||
            availabilityStatus.status === AVAILABILITY_STATUS.PRELIMINARY_CONFLICT ? 'error' :
            'info'
          }
          showIcon={false}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Checking material availability...</Text>
          </div>
        </div>
      )}

      {/* Material Requirements Table */}
      {!loading && tableData.length > 0 && (
        <>
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            size="small"
            scroll={{ x: true }}
          />
          
          {/* Summary Statistics */}
          <Divider />
          <Row gutter={16}>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">Total Material Types</Text>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                  {tableData.length}
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">Total Items</Text>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                  {tableData.reduce((sum, item) => sum + item.totalQuantity, 0)}
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">Manual Overrides</Text>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                  {Object.keys(manualOverrides).length}
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}

      {/* No Requirements Message */}
      {!loading && tableData.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              No material requirements calculated. Please ensure line items have working height and width specified.
            </Text>
          </div>
        </div>
      )}

      {/* Availability Suggestions */}
      {suggestions.length > 0 && (
        <>
          <Divider />
          <div style={{ marginTop: 16 }}>
            <Title level={5}>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
              Suggestions
            </Title>
            <List
              size="small"
              dataSource={suggestions}
              renderItem={(suggestion) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Badge 
                        status={suggestion.type === 'alternative_dates' ? 'processing' : 'warning'} 
                      />
                    }
                    title={
                      suggestion.type === 'alternative_dates' 
                        ? 'Alternative Dates Available' 
                        : 'Material Alternative'
                    }
                    description={suggestion.message}
                  />
                </List.Item>
              )}
            />
          </div>
        </>
      )}

      {/* Detailed Availability Information */}
      {availabilityResult && availabilityResult.details && (
        <>
          <Divider />
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Availability Details</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Date Source: </Text>
                <Tag color={availabilityResult.details.source === 'scheduled' ? 'green' : 'blue'}>
                  {availabilityResult.details.source === 'scheduled' ? 'Scheduled Dates' : 'Invoice Dates'}
                </Tag>
              </Col>
              <Col span={12}>
                <Text type="secondary">Priority: </Text>
                <Text>{availabilityResult.details.priority}</Text>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}>
                <Text type="secondary">Date Ranges Checked: </Text>
                <Text>{availabilityResult.details.dateRangesChecked}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Has Conflicts: </Text>
                <Text style={{ color: availabilityResult.details.hasConflicts ? '#ff4d4f' : '#52c41a' }}>
                  {availabilityResult.details.hasConflicts ? 'Yes' : 'No'}
                </Text>
              </Col>
            </Row>
          </div>
        </>
      )}

      {/* Add Material Modal */}
      <Modal
        title="Add Material"
        open={showAddMaterialModal}
        onOk={handleAddMaterial}
        onCancel={() => {
          setShowAddMaterialModal(false);
          addMaterialForm.resetFields();
        }}
        okText="Add"
        cancelText="Cancel"
      >
        <Form form={addMaterialForm} layout="vertical">
          <Form.Item
            name="materialType"
            label="Material Type"
            rules={[{ required: true, message: 'Please select a material type' }]}
          >
            <Select
              placeholder="Select material type"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {Object.values(MATERIAL_TYPES).map(materialType => (
                <Select.Option key={materialType} value={materialType}>
                  {materialType}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[
              { required: true, message: 'Please enter quantity' },
              { type: 'number', min: 1, message: 'Quantity must be at least 1' }
            ]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder="Enter quantity"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default MaterialListManager;
